'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Loader2, Images, ClipboardCheck } from 'lucide-react';

import { useRouter } from '@/i18n/routing';
import { API_ENDPOINTS } from '@/lib/api/config';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useImageZoomPan } from '@/hooks/use-image-zoom-pan';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUploadProgress } from '@/hooks/use-upload-progress';
import type { QrOverlayStatus } from '@/lib/qr/qr-overlay-styles';
import { QR_REVIEW_HASH } from '../constants';
import {
  useEnhancePageMutation,
  useUpdatePageQrScanMutation
} from '../services/qr-review.mutations';

import { useQrReviewSession } from './use-qr-review-session';
import { useQrScanFromImage } from './use-qr-scan-from-image';
import { QrReviewPageSidebar } from './qr-review-page-sidebar';
import { QrReviewImageViewer } from './qr-review-image-viewer';
import { QrReviewVerdictPanel } from './qr-review-verdict-panel';
import { QrReviewRejectDialog } from './qr-review-reject-dialog';

// ─── Props ───────────────────────────────────────────────────────────────────

interface QrReviewDetailPageProps {
  batchId: number;
  /**
   * When true, hides all action controls (Pass/Fail, End Session, enhance,
   * QR rescan) and skips the auto-start-review side effect. Used to embed
   * the scanning view inside other pages as a read-only reference.
   */
  readOnly?: boolean;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function QrReviewDetailPage({
  batchId,
  readOnly = false
}: QrReviewDetailPageProps) {
  const t = useTranslations('qrReview');
  const locale = useLocale();
  const router = useRouter();
  // Reject-batch dialog open-state lives in the URL hash (#reject) so it is
  // deep-linkable, survives refresh, and closes on browser Back.
  const { open: rejectDialogOpen, onOpenChange: toggleRejectDialog } =
    useHashDialog(QR_REVIEW_HASH.reject);
  // Enhancement panel open-state also lives in the URL hash (#enhance) —
  // deep-linkable and closes on browser Back.
  const { open: enhanceOpen, onOpenChange: setEnhanceOpen } = useHashDialog(
    QR_REVIEW_HASH.enhance
  );
  // Below `lg` there isn't room for the page-list sidebar (w-64) and the
  // verdict panel (w-80) next to the image viewer — same "full-width or a
  // Sheet below lg" rule as the other resizable-panel views. Both drop into
  // their own Sheet, deep-linkable the same way as the other overlays here.
  const isCompact = useIsMobile(1024);
  const { open: pagesOpen, onOpenChange: togglePages } = useHashDialog(
    QR_REVIEW_HASH.pages
  );
  const { open: verdictOpen, onOpenChange: toggleVerdict } = useHashDialog(
    QR_REVIEW_HASH.verdict
  );

  // ── Session (queries, mutations, navigation, verdict) ───────────────────
  const session = useQrReviewSession(batchId, { readOnly });
  const {
    batch,
    pages,
    selectedPageIndex,
    setSelectedPageIndex,
    selectedPage,
    canEndSession,
    progressPct,
    goToPrev,
    goToNext,
    handleManualVerdict,
    isVerdictPending,
    handleEndSession,
    isEndingSession
  } = session;

  // ── Image blob URL for the selected page ────────────────────────────────
  const imageUrl = selectedPage
    ? API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(batchId, selectedPage.id)
    : null;
  const mainImageBlobUrl = useAuthenticatedBlobUrl(imageUrl);

  // ── Zoom / Pan ──────────────────────────────────────────────────────────
  const {
    zoomLevel,
    setZoomLevel,
    panOffset,
    isDragging,
    reset: resetZoomPan,
    pointerHandlers
  } = useImageZoomPan();

  // ── QR scan from image ──────────────────────────────────────────────────
  const expectedQr =
    batch && selectedPage
      ? `${batch.container_id}-P${selectedPage.sequence}`
      : null;

  const qrScan = useQrScanFromImage(mainImageBlobUrl, expectedQr);

  // ── QR overlay status ───────────────────────────────────────────────────
  // Live decode wins when it returns a definitive answer; otherwise we
  // surface whatever the page row was last persisted with so the overlay
  // shows up instantly when the user lands on the page.
  const overlayStatus = useMemo<QrOverlayStatus | null>(() => {
    if (qrScan.qrScanResult === 'match') return 'success';
    if (qrScan.qrScanResult === 'mismatch') return 'mismatched';
    if (!selectedPage) return null;
    const persisted = selectedPage.qr_status;
    if (persisted === 'missing') return 'unverified';
    if (
      persisted === 'success' ||
      persisted === 'mismatched' ||
      persisted === 'unverified' ||
      persisted === 'failed'
    ) {
      return persisted;
    }
    return null;
  }, [qrScan.qrScanResult, selectedPage]);

  // ── Self-healing backfill ───────────────────────────────────────────────
  // When the client-side decoder lands on a row whose persisted corners
  // differ from what we just decoded (legacy rows have null corners), fire
  // the mutation once so subsequent loads render the overlay instantly
  // without re-decoding. Fingerprint guard prevents re-render thrash.
  const updateQrScanMutation = useUpdatePageQrScanMutation();
  const backfilledRef = useRef<string | null>(null);
  useEffect(() => {
    if (readOnly) return;
    if (!selectedPage) return;
    if (qrScan.qrScanning) return;
    const corners = qrScan.liveQrCorners;
    if (!corners) return;
    const fingerprint = `${selectedPage.id}|${corners
      .map((c) => `${c.x.toFixed(4)},${c.y.toFixed(4)}`)
      .join('|')}|${qrScan.scannedQrValue ?? ''}`;
    if (backfilledRef.current === fingerprint) return;
    const persistedCornersKey = selectedPage.qr_bbox?.corners
      ?.map((c) => `${c.x.toFixed(4)},${c.y.toFixed(4)}`)
      .join('|');
    const liveCornersKey = corners
      .map((c) => `${c.x.toFixed(4)},${c.y.toFixed(4)}`)
      .join('|');
    if (
      persistedCornersKey === liveCornersKey &&
      selectedPage.barcode === qrScan.scannedQrValue
    ) {
      backfilledRef.current = fingerprint;
      return;
    }
    backfilledRef.current = fingerprint;
    updateQrScanMutation.mutate({
      batchId,
      pageId: selectedPage.id,
      barcode: qrScan.scannedQrValue,
      qr_bbox: { corners }
    });
  }, [
    readOnly,
    selectedPage,
    qrScan.qrScanning,
    qrScan.liveQrCorners,
    qrScan.scannedQrValue,
    batchId,
    updateQrScanMutation
  ]);

  // ── Enhancement state ───────────────────────────────────────────────────
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [grayscale, setGrayscale] = useState(0);
  const [colorMode, setColorMode] = useState<
    'color' | 'grayscale' | 'document'
  >('color');
  const [sharpen, setSharpen] = useState(false);

  const enhanceMutation = useEnhancePageMutation();
  const {
    progress: enhanceProgress,
    onProgress: onEnhanceProgress,
    reset: resetEnhanceProgress
  } = useUploadProgress();

  const previewFilter =
    brightness !== 100 || contrast !== 100 || grayscale !== 0
      ? `brightness(${brightness}%) contrast(${contrast}%) grayscale(${grayscale}%)`
      : undefined;

  function handleColorMode(mode: 'color' | 'grayscale' | 'document') {
    setColorMode(mode);
    if (mode === 'color') setGrayscale(0);
    else if (mode === 'grayscale') setGrayscale(100);
    else {
      setGrayscale(100);
      setContrast(150);
    }
  }

  function handleAutoEnhance() {
    setBrightness(115);
    setContrast(140);
    setGrayscale(100);
    setColorMode('document');
  }

  function handleEnhanceReset() {
    setBrightness(100);
    setContrast(100);
    setGrayscale(0);
    setColorMode('color');
    setSharpen(false);
  }

  const handleEnhanceApply = useCallback(async () => {
    if (!mainImageBlobUrl || !selectedPage) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.src = mainImageBlobUrl;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
    });
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) grayscale(${grayscale}%)`;
    ctx.drawImage(img, 0, 0);
    ctx.filter = 'none';
    if (sharpen) {
      const kernel = [0, -1, 0, -1, 5, -1, 0, -1, 0];
      const d = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const r = new ImageData(new Uint8ClampedArray(d.data), d.width, d.height);
      for (let y = 1; y < d.height - 1; y++)
        for (let x = 1; x < d.width - 1; x++)
          for (let c = 0; c < 3; c++) {
            let s = 0;
            for (let ky = -1; ky <= 1; ky++)
              for (let kx = -1; kx <= 1; kx++)
                s +=
                  d.data[((y + ky) * d.width + (x + kx)) * 4 + c] *
                  kernel[(ky + 1) * 3 + (kx + 1)];
            r.data[(y * d.width + x) * 4 + c] = Math.min(255, Math.max(0, s));
          }
      ctx.putImageData(r, 0, 0);
    }
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        enhanceMutation.mutate(
          {
            batchId,
            pageId: selectedPage.id,
            imageBlob: blob,
            onProgress: onEnhanceProgress
          },
          {
            onSuccess: () => {
              handleEnhanceReset();
              setEnhanceOpen(false);
            },
            onSettled: resetEnhanceProgress
          }
        );
      },
      'image/jpeg',
      0.92
    );
  }, [
    mainImageBlobUrl,
    selectedPage,
    brightness,
    contrast,
    grayscale,
    sharpen,
    enhanceMutation,
    batchId,
    onEnhanceProgress,
    resetEnhanceProgress,
    setEnhanceOpen
  ]);

  const isEnhanceDisabled =
    !mainImageBlobUrl ||
    (brightness === 100 && contrast === 100 && grayscale === 0 && !sharpen) ||
    enhanceMutation.isPending;

  // ── Reset state on page change ──────────────────────────────────────────
  useEffect(() => {
    resetZoomPan();
    qrScan.resetScanState();
    handleEnhanceReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPageIndex]);

  // ── Keyboard navigation ─────────────────────────────────────────────────
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goToPrev, goToNext]);

  // ── Loading state ───────────────────────────────────────────────────────
  if (!batch) {
    return (
      <div
        className={
          readOnly
            ? 'flex h-[85svh] items-center justify-center'
            : 'flex h-screen items-center justify-center'
        }
      >
        <Loader2 className='text-muted-foreground h-8 w-8 animate-spin' />
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────
  const pagesSidebarNode = (
    <QrReviewPageSidebar
      pages={pages}
      selectedPageIndex={selectedPageIndex}
      onSelectPage={(index) => {
        setSelectedPageIndex(index);
        if (isCompact) togglePages(false);
      }}
      batchId={batchId}
      progressPct={progressPct}
      className={isCompact ? 'w-full' : undefined}
    />
  );

  const verdictPanelNode = (
    <QrReviewVerdictPanel
      batch={batch}
      selectedPage={selectedPage}
      pages={pages}
      qrScanning={qrScan.qrScanning}
      qrScanResult={qrScan.qrScanResult}
      scanMismatch={qrScan.scanMismatch}
      scannedQrValue={qrScan.scannedQrValue}
      onRescanQr={qrScan.triggerRescan}
      onManualVerdict={handleManualVerdict}
      isVerdictPending={isVerdictPending}
      canEndSession={canEndSession}
      isEndingSession={isEndingSession}
      onEndSession={handleEndSession}
      onRejectBatch={
        batch.qa_status === 'in_qa' || batch.qa_status === 'pending_qa'
          ? () => toggleRejectDialog(true)
          : undefined
      }
      canRejectBatch={
        batch.qa_status === 'in_qa' || batch.qa_status === 'pending_qa'
      }
      readOnly={readOnly}
      className={isCompact ? 'w-full' : undefined}
    />
  );

  return (
    <div
      className={
        readOnly
          ? 'relative flex h-[85svh] overflow-hidden'
          : 'relative flex h-screen overflow-hidden'
      }
    >
      {!isCompact && pagesSidebarNode}

      <QrReviewImageViewer
        mainImageBlobUrl={mainImageBlobUrl}
        selectedPage={selectedPage}
        selectedPageIndex={selectedPageIndex}
        totalPages={pages.length}
        onPrev={goToPrev}
        onNext={goToNext}
        zoomLevel={zoomLevel}
        setZoomLevel={setZoomLevel}
        panOffset={panOffset}
        isDragging={isDragging}
        pointerHandlers={pointerHandlers}
        enhanceOpen={enhanceOpen}
        setEnhanceOpen={setEnhanceOpen}
        previewFilter={previewFilter}
        brightness={brightness}
        setBrightness={setBrightness}
        contrast={contrast}
        setContrast={setContrast}
        colorMode={colorMode}
        onColorMode={handleColorMode}
        sharpen={sharpen}
        setSharpen={setSharpen}
        onAutoEnhance={handleAutoEnhance}
        onEnhanceReset={handleEnhanceReset}
        onEnhanceApply={() => void handleEnhanceApply()}
        isEnhancing={enhanceMutation.isPending}
        isEnhanceDisabled={isEnhanceDisabled}
        enhanceProgress={enhanceProgress}
        qrStatus={overlayStatus}
        qrBbox={selectedPage?.qr_bbox ?? null}
        liveQrCorners={qrScan.liveQrCorners}
        qrLabel={qrScan.scannedQrValue ?? selectedPage?.barcode ?? null}
        readOnly={readOnly}
      />

      {!isCompact && verdictPanelNode}

      {/* Below `lg` — floating triggers for the two panels dropped above.
          Mirrors the OCR results page's FAB+Sheet pattern. */}
      {isCompact && (
        <>
          <Button
            size='sm'
            variant='secondary'
            className='absolute start-3 top-3 z-20 shadow-lg'
            onClick={() => togglePages(true)}
            aria-label={t('sectionDocumentPages')}
          >
            <Images className='h-4 w-4' />
            <span className='ms-2'>
              {progressPct}% {t('metricCompleted')}
            </span>
          </Button>

          <Button
            size='sm'
            variant='secondary'
            className='absolute end-3 top-3 z-20 shadow-lg'
            onClick={() => toggleVerdict(true)}
            aria-label={t('sectionPageVerdict')}
          >
            <ClipboardCheck className='h-4 w-4' />
            <span className='ms-2'>{t('sectionPageVerdict')}</span>
          </Button>

          <Sheet open={pagesOpen} onOpenChange={togglePages}>
            <SheetContent
              side={locale === 'ar' ? 'right' : 'left'}
              className='w-full gap-0 p-0 sm:max-w-xs'
            >
              <SheetTitle className='sr-only'>
                {t('sectionDocumentPages')}
              </SheetTitle>
              {pagesSidebarNode}
            </SheetContent>
          </Sheet>

          <Sheet open={verdictOpen} onOpenChange={toggleVerdict}>
            <SheetContent
              side={locale === 'ar' ? 'left' : 'right'}
              className='w-full gap-0 p-0 sm:max-w-sm'
            >
              <SheetTitle className='sr-only'>
                {t('sectionPageVerdict')}
              </SheetTitle>
              {verdictPanelNode}
            </SheetContent>
          </Sheet>
        </>
      )}

      {/* Reject Dialog — #reject */}
      <QrReviewRejectDialog
        open={rejectDialogOpen}
        onOpenChange={toggleRejectDialog}
        batchId={batchId}
        onSuccess={() => router.push('/dashboard/qr-review')}
      />
    </div>
  );
}
